import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  MenuItem,
  Button,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import {
  Checklist,
  FlightTakeoff,
  Hotel,
  DirectionsCar,
  Home,
  Badge,
  DirectionsBus,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  resolveProductLine,
  getServiceVisibility,
  getOriginAirports,
  getDestinationAirports,
} from '../../../utils/productLineConfig';

const carClasses = ['Economy', 'Sedan', 'SUV', 'Van'];

const ServiceCard = ({ icon, title, description, checked, onToggle, children }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 2,
      height: '100%',
      borderColor: checked ? 'secondary.main' : 'divider',
      borderWidth: checked ? 2 : 1,
      boxShadow: checked ? '0 8px 24px rgba(31,111,120,0.12)' : 'none',
    }}
  >
    <CardContent>
      <FormControlLabel
        control={<Checkbox checked={checked} onChange={onToggle} color="secondary" />}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: 'secondary.main', display: 'flex' }}>{icon}</Box>
            <Box>
              <Typography fontWeight={700}>{title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {description}
              </Typography>
            </Box>
          </Box>
        }
        sx={{ alignItems: 'flex-start', mb: checked && children ? 2 : 0, width: '100%', ml: 0 }}
      />
      {checked && children && (
        <Box sx={{ pl: { xs: 0, sm: 1 }, pt: 1 }}>
          <Divider sx={{ mb: 2 }} />
          {children}
        </Box>
      )}
    </CardContent>
  </Card>
);

const ServicesStepV2 = ({ bookingData, updateBookingData }) => {
  const navigate = useNavigate();
  const productLine = resolveProductLine(bookingData);
  const vis = getServiceVisibility(productLine);
  const originAirports = getOriginAirports();
  const destinationAirports = getDestinationAirports(
    productLine,
    bookingData.destinationCountry
  );

  const toggle = (key) => () => {
    const next = !bookingData[key];
    const patch = { [key]: next };
    if (key === 'includeTransport' && !next) {
      patch.includeHomePickup = false;
    }
    if (key === 'includeFlight' && !next) {
      patch.selectedFlightOfferId = null;
      patch.selectedFlightAmount = null;
      patch.selectedFlightCurrency = null;
      patch.selectedFlightLabel = null;
    }
    updateBookingData(patch);
  };

  const visaOptions =
    productLine === 'Umrah' ? ['Umrah', 'Tourist', 'Visit'] : ['Tourist', 'Visit', 'Business'];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Checklist color="primary" />
        Choose your services
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Product: <strong>{productLine}</strong> — only relevant options are shown. Tick what you need.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        You can book a single service or combine several.
      </Typography>

      <Grid container spacing={2}>
        {vis.showFlight && (
        <Grid item xs={12} md={6}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <ServiceCard
              icon={<FlightTakeoff />}
              title="Flight ticket"
              description="Live indicative fares (agent confirms on portal)"
              checked={!!bookingData.includeFlight}
              onToggle={toggle('includeFlight')}
            >
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="From"
                    value={bookingData.flightOrigin || 'ISB'}
                    onChange={(e) => updateBookingData({ flightOrigin: e.target.value })}
                  >
                    {originAirports.map((a) => (
                      <MenuItem key={a.code} value={a.code}>
                        {a.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="To"
                    value={bookingData.flightDestination || destinationAirports[0]?.code}
                    onChange={(e) => updateBookingData({ flightDestination: e.target.value })}
                  >
                    {destinationAirports.map((a) => (
                      <MenuItem key={`to-${a.code}`} value={a.code}>
                        {a.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                {bookingData.selectedFlightLabel ? (
                  <Chip
                    color="secondary"
                    label={`${bookingData.selectedFlightLabel} · ${bookingData.selectedFlightCurrency || 'USD'} ${bookingData.selectedFlightAmount || ''}`}
                    onDelete={() =>
                      updateBookingData({
                        selectedFlightOfferId: null,
                        selectedFlightAmount: null,
                        selectedFlightCurrency: null,
                        selectedFlightLabel: null,
                      })
                    }
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No fare selected yet — search live prices, then return here.
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<FlightTakeoff />}
                  onClick={() => {
                    try {
                      sessionStorage.setItem(
                        'customizeBookingDraft',
                        JSON.stringify({ ...bookingData, includeFlight: true })
                      );
                      sessionStorage.setItem('customizeBookingStep', '1');
                    } catch (_) {
                      /* ignore */
                    }
                    navigate('/v2/flights', {
                      state: {
                        origin: bookingData.flightOrigin || 'ISB',
                        destination: bookingData.flightDestination || 'JED',
                        departureDate: bookingData.departureDate || '',
                        adults: bookingData.adults || 1,
                        children: bookingData.children || 0,
                        infants: bookingData.infants || 0,
                        cabinClass: 'economy',
                        autoSearch: Boolean(bookingData.departureDate),
                        returnToCustomize: true,
                        flightScope: productLine,
                        destinationCountry: bookingData.destinationCountry,
                      },
                    });
                  }}
                >
                  Search flights
                </Button>
              </Stack>
            </ServiceCard>
          </motion.div>
        </Grid>
        )}

        {vis.showHotel && (
        <Grid item xs={12} md={6}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <ServiceCard
              icon={<Hotel />}
              title="Hotel / stay"
              description="Destinations and nights on the next step"
              checked={bookingData.includeHotel !== false}
              onToggle={toggle('includeHotel')}
            >
              <Typography variant="body2" color="text.secondary">
                Configure cities and nights in <strong>Destinations & Duration</strong>.
              </Typography>
            </ServiceCard>
          </motion.div>
        </Grid>
        )}

        {vis.showTransport && (
        <Grid item xs={12} md={6}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <ServiceCard
              icon={<DirectionsCar />}
              title="Transport / transfer"
              description="Airport or city transfers"
              checked={!!bookingData.includeTransport}
              onToggle={toggle('includeTransport')}
            >
              {vis.showHomePickup && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!bookingData.includeHomePickup}
                    onChange={() =>
                      updateBookingData({ includeHomePickup: !bookingData.includeHomePickup })
                    }
                    color="secondary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Home fontSize="small" color="action" />
                    <Box>
                      <Typography fontWeight={600}>Home pickup</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pickup from your home / hotel address
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Vehicle type is chosen in Transportation & Contact.
              </Typography>
            </ServiceCard>
          </motion.div>
        </Grid>
        )}

        {vis.showVisa && (
        <Grid item xs={12} md={6}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <ServiceCard
              icon={<Badge />}
              title="Visa service"
              description="We process / assist your visa"
              checked={!!bookingData.includeVisa}
              onToggle={toggle('includeVisa')}
            >
              <TextField
                select
                fullWidth
                size="small"
                label="Visa type"
                value={bookingData.visaType || (productLine === 'Umrah' ? 'Umrah' : 'Tourist')}
                onChange={(e) => updateBookingData({ visaType: e.target.value })}
              >
                {visaOptions.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </TextField>
            </ServiceCard>
          </motion.div>
        </Grid>
        )}

        {vis.showCarRental && (
        <Grid item xs={12} md={6}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <ServiceCard
              icon={<DirectionsBus />}
              title="Rent a car"
              description="Self-drive or with driver (quoted)"
              checked={!!bookingData.includeCarRental}
              onToggle={toggle('includeCarRental')}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Pickup city"
                  value={bookingData.carRentalCity || ''}
                  onChange={(e) => updateBookingData({ carRentalCity: e.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Days"
                  value={bookingData.carRentalDays || 3}
                  onChange={(e) =>
                    updateBookingData({ carRentalDays: Math.max(1, Number(e.target.value) || 1) })
                  }
                  inputProps={{ min: 1, max: 30 }}
                />
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Class"
                  value={bookingData.carRentalClass || 'Sedan'}
                  onChange={(e) => updateBookingData({ carRentalClass: e.target.value })}
                >
                  {carClasses.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </ServiceCard>
          </motion.div>
        </Grid>
        )}

        {vis.showZiaraat && (
          <Grid item xs={12} md={6}>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <ServiceCard
                icon={<Hotel />}
                title="Ziaraat"
                description="Religious sites tours in Makkah / Madina"
                checked={!!bookingData.includeZiaraat}
                onToggle={toggle('includeZiaraat')}
              >
                <Typography variant="body2" color="text.secondary">
                  Preference (Standard / Premium / Private) is set in Transportation & Contact.
                </Typography>
              </ServiceCard>
            </motion.div>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ServicesStepV2;
